import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { useScenarioStore } from '../../pages/scenario-modeling/scenario-modeling.store';
import { ApiService } from '../../services/index';
import {
  LiquidityCapTableByShareClass,
  ScenarioCapTableByShareClass,
  ScenarioType,
} from '../../types/scenario-modeling.types';
import { useAppQuery } from '../use-app-query.hook';

export const useCapTableShareClasses = <T extends ScenarioType = ScenarioType.FUNDRAISING>({
  includeSearch = false,
  scenarioId: customScenarioId,
}: { includeSearch?: boolean; scenarioId?: string } = {}) => {
  const queryClient = useQueryClient();
  const { scenarioId: defaultScenarioId, search } = useScenarioStore();
  const scenarioId = customScenarioId || defaultScenarioId;

  const { data, isLoading, isSuccess } = useAppQuery<
    (T extends ScenarioType.FUNDRAISING
      ? ScenarioCapTableByShareClass
      : LiquidityCapTableByShareClass)[]
  >({
    queryKey: [QueryKey.CAP_TABLE_SHARE_CLASSES, scenarioId, includeSearch, search],
    queryFn: () =>
      ApiService.get(
        {
          endpoint: BackendRoute.SCENARIOS,
          routePath: [scenarioId, 'cap-table', 'share-classes'],
        },
        includeSearch ? { queryParams: { search } } : undefined,
      ),
    refetchOnWindowFocus: false,
    // Tab remounts must render cached rows instantly — see use-cap-table-stakeholder-by-all.
    staleTime: 30_000,
    enabled: !!scenarioId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.CAP_TABLE_SHARE_CLASSES] });
  }, [queryClient]);

  return {
    invalidateQuery,
    data,
    isLoading,
    isSuccess,
    isEmptyState: Boolean(search && !data?.length),
  };
};
